/*
  Scroll reveal — reveals [data-reveal] / [data-reveal-stagger] each time one
  enters the viewport, and re-arms it once it has fully left, so the entrance
  replays on every pass rather than only on first sight. Above-the-fold content
  is revealed by the observer's first callback, which is what turns this into a
  page-load entrance for the hero.

  Not a custom element, deliberately. There is no per-element behaviour to
  encapsulate here: one document-level IntersectionObserver watching every target
  is cheaper than an element instance per card, and the theme already uses this
  pattern in component-sticky-offer-bar.js.

  Not pure CSS either, though `animation-timeline: view()` can express a reveal.
  A view timeline scrubs with scroll position, so a fast flick lands every card
  at once and the cascade disappears. The rhythm below is time-based on purpose.
*/
if (!window.__ThemeReveal && "IntersectionObserver" in window) {
  window.__ThemeReveal = true;

  // Without IntersectionObserver, reveal-init.liquid never sets `reveal-ready`,
  // so nothing is hidden and there is nothing for this script to do. Bailing at
  // the top matters: reaching getObserver() would throw on the constructor and
  // put a red error in the console of every browser too old to support it.

  const REVEAL_SELECTOR = "[data-reveal], [data-reveal-stagger]";

  // Zero margin, zero threshold: reveal the moment any part of the element
  // touches the viewport, re-arm only once every part of it has left.
  //
  // An earlier version shrank the bottom edge by 10% so the rise finished as the
  // eye arrived. That is a nicer entrance, but it cannot survive a reveal that
  // replays: the shrunk edge means an element is "not intersecting" while still
  // physically on screen in the bottom 10%, so scrolling up would blank content
  // the customer is looking at. Correctness beats the flourish.
  const OBSERVER_OPTIONS = {
    rootMargin: "0px",
    threshold: 0,
  };

  const reveal = (el) => {
    el.classList.add("is-revealed");
  };

  // Re-arms the element so it rises again next time it comes back into view.
  // Only ever runs while the element is fully off screen, so the reset itself is
  // never visible -- the customer sees a fresh entrance, not a disappearance.
  const rearm = (el) => {
    el.classList.remove("is-revealed");
  };

  // Reduced motion is handled in CSS, but there is no reason to run an observer
  // whose only job is to trigger transitions that resolve to `none`.
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let observer = null;

  const getObserver = () => {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Targets stay observed for the life of the page: the reveal replays
        // every time a section comes back into view, so it is never unobserved.
        if (entry.isIntersecting) reveal(entry.target);
        else rearm(entry.target);
      });
    }, OBSERVER_OPTIONS);
    return observer;
  };

  const scan = (scope = document) => {
    const targets = scope.querySelectorAll(REVEAL_SELECTOR);
    if (prefersReducedMotion()) {
      targets.forEach(reveal);
      return;
    }
    const io = getObserver();
    // observe() on an already-observed target is a no-op, so a re-scan after a
    // theme-editor reload costs nothing and needs no bookkeeping of its own.
    targets.forEach((el) => io.observe(el));
  };

  const start = () => {
    // Disarms the failsafe in snippets/reveal-init.liquid. Until this runs, that
    // timer is the promise that a broken build cannot leave the page blank --
    // exactly the failure mode this feature is prone to.
    window.__revealReady?.();
    scan();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  // The theme editor re-renders a section into fresh nodes, which the observer
  // has never seen. Without this, editing a section leaves it invisible.
  document.addEventListener("shopify:section:load", (e) => scan(e.target));

  // Scripts that replace a section themselves (the plan builder's deep-link reconciler
  // swaps its whole section for a fresh render) dispatch this on the new nodes. Same
  // problem as the editor reload above: unscanned targets sit at opacity 0 forever.
  document.addEventListener("reveal:scan", (e) => scan(e.target));
}
